import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  ActivityIndicator,
  FlatList,
  useWindowDimensions,
} from 'react-native';
import { AppAlert } from '../../services/AppAlert';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import useLocation from '../../hooks/useLocation';
import { useAppSelector } from '../../redux/helper';
import { s, iconSize, fs, v, borderWidth } from '../../utils/libs';
import { isEmployeeAdminRole } from '../../utils/roles';
import { useNavigation } from '@react-navigation/native';
import ErrorContext from '../../Context/ErrorContext';
import { GTFonts, PoppinsFonts, theme } from '../../theme';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import LocationBottomSheet from './LocationBottomSheet';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../redux/Store';

type FontAwesome6SolidIconName = Extract<
  React.ComponentProps<typeof FontAwesome6>,
  { iconStyle: 'solid' }
>['name'];

// Employee (POS) slices
import { ClearCart, clearCartOrderData } from '../../redux/cartSlice';
import {
  clearCompletedOrders,
  clearPendingOrders,
  clearPartialOrders,
  clearFilteredCompletedOrder,
  clearFilteredPendingOrder,
  clearFilteredPartialOrder,
} from '../../redux/OrdersSlice';
import { clearCheckout } from '../../redux/checkoutSlice';
import { clearPartialPaymentsDetails } from '../../redux/partialPaymentsSlice';
import { clearOrderDetails } from '../../redux/orderDetailsSlice';
import { addMpesaPayments } from '../../redux/mpesaPaymentSlice';
import { resetProductSort } from '../../redux/productSortSlice';
import {
  clearAllProducts,
  resetSearchProducts,
  clearCategories,
  clearSubCategories,
  clearProductsNames,
  clearProductsBySubCategory,
  clearSearchedProductsFromScanner,
} from '../../redux/productSlice';

// Backoffice (owner) slices
import {
  clearCompleted as boClearCompleted,
  clearPending as boClearPending,
  clearPartial as boClearPartial,
  clearTableSearch as boClearTableSearch,
} from '../../redux/backoffice/ordersSlice';
import { clearSummary } from '../../redux/backoffice/summarySlice';
import {
  clearTableSearch as boProductsClearTableSearch,
  setProducts as boSetProducts,
} from '../../redux/backoffice/productsSlice';
import {
  setCurrentAndLastMonth,
  setDailyPayments,
  setRevenueOverTime,
  setWeeklySalesData,
  setProductsReport,
} from '../../redux/backoffice/reportsSlice';
import { clearReport } from '../../redux/backoffice/reportsTableSlice';
import { clearInvoices } from '../../redux/backoffice/billingSlice';
import { setEmployees } from '../../redux/backoffice/employeesSlice';
import {
  setMonthlySales,
  setSalesOverTime,
  setCategorySales,
  setTransactions,
} from '../../redux/backoffice/salesSlice';
import { setDashboardData } from '../../redux/backoffice/dashboardSlice';
import {
  setMonthlyData,
  setAnnualPoints,
  setCategoryItems,
  setSummaryData,
  resetExpensesList,
  setDetailData,
} from '../../redux/backoffice/expensesSlice';
import {
  setTargetAmount,
  setTargetError,
  setTargetLoading,
} from '../../redux/backoffice/targetsSlice';
import {
  setIsAllLocations,
  setLocationSwitching,
  incrementLocationVersion,
} from '../../redux/locationSlice';
import { selectLocationPickerOpenRequest } from '../../redux/uiSlice';

interface LocationPickerProps {
  onLocationChange?: (locationId: string) => void;
  variant?: 'default' | 'compact';
  textColor?: string; // used for compact variant in headers
  showAllLocationsOption?: boolean; // Show "All Locations" option for backoffice aggregate queries
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  onLocationChange,
  variant = 'default',
  textColor,
  showAllLocationsOption = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const isAllLocations = useAppSelector(state => state.location.isAllLocations);
  const currentLocationData = useAppSelector(
    state => state.location.currentLocation,
  );
  const employeedata = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );
  const currentLocationId = useAppSelector(
    state => state.location.currentLocationId,
  );
  const { locations, isLocationSwitching, switchLocation, isAdmin } =
    useLocation();

  const [modalVisible, setModalVisible] = useState(false);
  const bottomSheetRef = useRef<BottomSheetModal | null>(null);
  const navigation = useNavigation<any>();
  const { handleError } = useContext(ErrorContext);

  // Imperative open from the activation tracker's "Visit test location" task.
  // Only the header (compact, admin) instance should respond, so it doesn't
  // double-present when multiple pickers are mounted.
  const openRequest = useAppSelector(selectLocationPickerOpenRequest);
  const lastHandledOpenRequest = useRef(openRequest);
  useEffect(() => {
    if (openRequest === lastHandledOpenRequest.current) return;
    lastHandledOpenRequest.current = openRequest;
    if (variant !== 'compact' || !isAdmin || isLocationSwitching) return;
    setModalVisible(true);
    requestAnimationFrame(() => bottomSheetRef.current?.present?.());
  }, [openRequest, variant, isAdmin, isLocationSwitching]);

  const clearOwnerState = () => {
    try {
      // Backoffice orders
      dispatch(boClearCompleted());
      dispatch(boClearPending());
      dispatch(boClearPartial());
      dispatch(boClearTableSearch());

      // Backoffice products
      dispatch(boProductsClearTableSearch());
      dispatch(boSetProducts({ items: [], total: 0, page: 1, pageSize: 500 }));

      // Backoffice summary
      dispatch(clearSummary());

      // Backoffice reports
      dispatch(setDailyPayments([]));
      dispatch(setRevenueOverTime(null));
      dispatch(setWeeklySalesData([] as any));
      dispatch(
        setCurrentAndLastMonth({
          lastMonth: { totalSales: 0 },
          currentMonth: { totalSales: 0 },
        }),
      );
      dispatch(
        setProductsReport({
          docs: [],
          totalDocs: 0,
          totalPages: 0,
          page: 1,
          perPage: 0,
          metadata: {
            totalItemsSold: 0,
            totalAmountWasted: 0,
            totalItemsWasted: 0,
            totalUpdatedProducts: 0,
            totalUpdatedProductsAmount: 0,
            totalSalesMade: 0,
          },
        } as any),
      );

      // Backoffice reports table
      dispatch(clearReport());

      // Backoffice billing
      dispatch(clearInvoices());

      // Backoffice employees
      dispatch(setEmployees({ items: [], total: 0, page: 1, pageSize: 100 }));

      // Backoffice sales
      dispatch(setMonthlySales([]));
      dispatch(setSalesOverTime(null));
      dispatch(setCategorySales([]));
      dispatch(
        setTransactions({
          items: [],
          total: 0,
          page: 1,
          pageSize: 100,
          totalPages: 0,
          totalAmount: 0,
        }),
      );

      // Backoffice dashboard
      dispatch(
        setDashboardData({
          totalSales: 0,
          totalQuantitiesSold: 0,
          totalMassSold: 0,
          totalProfit: 0,
          totalOrders: 0,
          totalExpenses: 0,
        }),
      );

      // Backoffice expenses
      dispatch(setMonthlyData({ payments: 0, stocks: 0, others: 0 }));
      dispatch(setAnnualPoints([]));
      dispatch(setCategoryItems([]));
      dispatch(setSummaryData(null));
      dispatch(resetExpensesList());
      dispatch(setDetailData(null));

      // Backoffice targets
      dispatch(setTargetLoading(false));
      dispatch(setTargetError(null));
      dispatch(setTargetAmount(null));
    } catch (e) {
      console.warn('clearOwnerState error', e);
    }
  };

  const clearEmployeeState = () => {
    try {
      // Orders (POS)
      dispatch(clearCompletedOrders());
      dispatch(clearPendingOrders());
      dispatch(clearPartialOrders());
      dispatch(clearFilteredCompletedOrder());
      dispatch(clearFilteredPendingOrder());
      dispatch(clearFilteredPartialOrder());

      // Cart & checkout
      dispatch(ClearCart());
      dispatch(clearCartOrderData());
      dispatch(clearCheckout());
      dispatch(clearPartialPaymentsDetails());
      dispatch(clearOrderDetails());
      dispatch(addMpesaPayments([]));
      dispatch(resetProductSort());

      // Products (POS)
      dispatch(clearAllProducts());
      dispatch(resetSearchProducts());
      dispatch(clearCategories());
      dispatch(clearSubCategories());
      dispatch(clearProductsNames());
      dispatch(clearProductsBySubCategory());
      dispatch(clearSearchedProductsFromScanner());
    } catch (e) {
      console.warn('clearEmployeeState error', e);
    }
  };

  const handleSelectAllLocations = async () => {
    setModalVisible(false);
    bottomSheetRef.current?.dismiss();

    // If already in all locations mode, do nothing
    if (isAllLocations) {
      return;
    }

    dispatch(setLocationSwitching(true));

    try {
      // Clear backoffice state first
      clearOwnerState();

      // Wait for React to process the clear actions
      await new Promise(resolve => setTimeout(resolve, 150));

      // Set the flag (this also increments locationVersion which triggers refetches)
      dispatch(setIsAllLocations(true));

      // Wait for screens to start their fetch operations
      await new Promise(resolve => setTimeout(resolve, 300));
    } finally {
      dispatch(setLocationSwitching(false));
    }
  };

  const handleLocationSelect = async (locationId?: string) => {
    // Check if user has permission to switch locations
    if (!isEmployeeAdminRole(employeedata?.bp_employee_role)) {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();
      handleError(
        'You do not have permission to switch locations.',
        'error',
        true,
      );
      return;
    }

    if (!locationId) {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();
      AppAlert.alert('Error', 'Invalid location selected.');
      return;
    }
    // Check if trying to switch to the same location (only if not in all locations mode)
    if (
      !isAllLocations &&
      currentLocationId &&
      locationId === currentLocationId
    ) {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();
      AppAlert.alert('Same Location', 'You are already in this location.', [
        { text: 'OK' },
      ]);
      return;
    }

    try {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();

      // Turn off all locations mode if it was on
      if (isAllLocations) {
        dispatch(setIsAllLocations(false));
      }

      await switchLocation(locationId);

      // Clear state tied to previous location
      clearOwnerState();
      clearEmployeeState();

      // Increment version to trigger refetches
      dispatch(incrementLocationVersion());

      if (onLocationChange) {
        onLocationChange(locationId);
      }

      const newLocation = locations?.find(
        location => location._id === locationId,
      );
      const isTestLocation = !!newLocation?.bp_location_is_system_generated;

      // Show success message and reload app data
      AppAlert.alert(
        isTestLocation ? 'You are on a test location' : 'Location Changed',
        isTestLocation
          ? `You are now on ${newLocation?.bp_location_name}, a test location. This is a safe place to explore BizPlus POS — it does not affect your real business data.`
          : `Location switched successfully. ${newLocation ? `You are now on ${newLocation?.bp_location_name}` : ''} `,
        [
          {
            text: 'OK',
            onPress: () => {
              // Trigger app reload/refresh logic
              if (onLocationChange) {
                onLocationChange(locationId);
              }
            },
          },
        ],
      );
    } catch (error) {
      AppAlert.alert('Error', 'Failed to switch location. Please try again.');
      console.error('Location switch error:', error);
    }
  };

  const getLocationIcon = (
    locationType?: string,
  ): FontAwesome6SolidIconName => {
    const type = locationType?.toLowerCase() || '';
    switch (type) {
      case 'outlet':
      case 'shop':
        return 'store';
      case 'warehouse':
        return 'warehouse';
      case 'office':
        return 'building';
      default:
        return 'location-dot';
    }
  };

  if (!isAdmin) {
    return null;
  }

  const renderLocationItem = ({ item }: { item: any }) => {
    // Don't highlight current location if in "All Locations" mode
    const isCurrentLocation =
      !isAllLocations &&
      currentLocationId &&
      (item.bp_location_id === currentLocationId ||
        item._id === currentLocationId);

    return (
      <Pressable
        onPress={() => handleLocationSelect(item.bp_location_id || item._id)}
        style={({ pressed, hovered }: any) => [
          styles.locationItem,
          Platform.OS === 'web' && styles.locationItemWeb,
          Platform.OS === 'web' && hovered && styles.locationItemHovered,
          pressed && { opacity: 0.85 },
        ]}>
        <View
          style={[
            styles.locationItemContent,
            isCurrentLocation && styles.currentLocationItem,
          ]}>
          <FontAwesome6
            name={getLocationIcon(item.bp_location_type)}
            iconStyle="solid"
            size={iconSize(20)}
            color={isCurrentLocation ? theme.colors.successDark : '#1560bd'}
            style={styles.locationIcon}
          />
          <View style={styles.locationInfo}>
            <Text style={styles.locationName}>{item.bp_location_name}</Text>
            <Text style={styles.locationDetails}>
              {item.bp_location_code} • {item.bp_location_type}
            </Text>
          </View>
          <View style={styles.badgeContainer}>
            {item.bp_location_is_default && (
              <View style={styles.defaultBadge}>
                <Text style={styles.defaultBadgeText}>Default</Text>
              </View>
            )}
            {isCurrentLocation && (
              <FontAwesome6
                name="circle-check"
                iconStyle="solid"
                size={iconSize(20)}
                color="#22c55e"
                style={styles.currentLocationIcon}
              />
            )}
          </View>
        </View>
      </Pressable>
    );
  };

  const isCompact = variant === 'compact';

  return (
    <View style={[styles.container, isCompact && styles.containerCompact]}>
      <TouchableOpacity
        style={[styles.selector, isCompact && styles.selectorCompact]}
        onPress={() => {
          setModalVisible(true);
          requestAnimationFrame(() => bottomSheetRef.current?.present?.());
        }}
        disabled={isLocationSwitching}>
        {/* <FontAwesome6
          name={getLocationIcon(currentLocationData?.bp_location_type)}
          size={iconSize(16)}
          color={isCompact ? textColor || '#fff' : '#1560bd'}
        /> */}
        {isAllLocations && showAllLocationsOption && (
          <FontAwesome6
            name="layer-group"
            iconStyle="solid"
            size={iconSize(14)}
            color={isCompact ? textColor || '#fff' : '#1560bd'}
            style={{ marginRight: s(4) }}
          />
        )}
        <Text
          style={[
            // styles.currentLocationText,
            isCompact && {
              color: textColor || '#fff',
              fontSize: fs(Platform.OS === 'web' ? 14 : 16),
            },
          ]}
          numberOfLines={1}>
          {(() => {
            if (isAllLocations && showAllLocationsOption)
              return 'All Locations';
            const name = currentLocationData?.bp_location_name;
            if (!name) return 'Select Location';
            const maxLen = Platform.OS === 'web' ? 35 : 25;
            return name.length > maxLen ? name.slice(0, maxLen) + '...' : name;
          })()}
        </Text>
        {isLocationSwitching ? (
          <ActivityIndicator
            size="small"
            color={isCompact ? textColor || '#fff' : '#1560bd'}
          />
        ) : (
          <FontAwesome6
            name="chevron-down"
            iconStyle="solid"
            size={iconSize(12)}
            color={isCompact ? textColor || '#fff' : '#1560bd'}
          />
        )}
      </TouchableOpacity>
      <LocationBottomSheet
        bottomSheetRef={bottomSheetRef}
        onClose={() => setModalVisible(false)}
        footer={
          <View style={styles.footerContainer}>
            {!isEmployeeAdminRole(employeedata?.bp_employee_role) ? null : (
              <TouchableOpacity
                onPress={() => {
                  setModalVisible(false);
                  bottomSheetRef.current?.dismiss();
                  navigation.navigate('NewLocation');
                }}
                style={styles.addNewLocationButton}>
                <Text style={styles.addNewLocationButtonText}>
                  + Add New Business
                </Text>
              </TouchableOpacity>
            )}
          </View>
        }>
        {/* All Locations option - only show when prop is enabled */}
        {showAllLocationsOption && (
          <>
            <TouchableOpacity
              style={styles.locationItem}
              onPress={handleSelectAllLocations}>
              <View
                style={[
                  styles.locationItemContent,
                  { borderColor: '#e0e7ff', backgroundColor: '#f5f3ff' },
                  isAllLocations && styles.currentLocationItem,
                ]}>
                <FontAwesome6
                  name="layer-group"
                  iconStyle="solid"
                  size={iconSize(20)}
                  color={isAllLocations ? theme.colors.successDark : '#6366f1'}
                  style={styles.locationIcon}
                />
                <View style={styles.locationInfo}>
                  <Text style={styles.locationName}>All Locations</Text>
                  <Text style={styles.locationDetails}>
                    Tap to view business-wide data
                  </Text>
                </View>
                {isAllLocations && (
                  <FontAwesome6
                    name="circle-check"
                    iconStyle="solid"
                    size={iconSize(20)}
                    color="#22c55e"
                    style={styles.currentLocationIcon}
                  />
                )}
              </View>
            </TouchableOpacity>
            <View
              style={{
                height: 1,
                backgroundColor: '#e5e7eb',
                marginVertical: v(8),
              }}
            />
          </>
        )}
        <FlatList
          data={locations}
          renderItem={renderLocationItem}
          keyExtractor={item => item._id ?? ''}
          style={styles.locationsList}
          showsVerticalScrollIndicator={false}
        />
      </LocationBottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: v(15),
  },
  containerCompact: {
    marginBottom: 0,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    paddingHorizontal: s(15),
    paddingVertical: s(10),
    borderRadius: s(8),
    borderWidth: borderWidth(2),
    borderColor: 'rgba(21, 96, 189, 0.5)',
    gap: s(8),
  },
  selectorCompact: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingHorizontal: 0,
    paddingVertical: s(2),
    gap: s(6),
  },
  currentLocationText: {
    flex: 1,
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: '#1560bd',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: s(15),
    padding: s(20),
    width: '90%',
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: v(20),
    paddingBottom: v(15),
    borderBottomWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderBottomColor: '#eee',
  },
  modalTitle: {
    fontSize: fs(20),
    fontFamily: GTFonts.regular,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    padding: s(5),
  },
  locationsList: {
    // maxHeight: v(400),
  },
  locationItem: {
    marginBottom: v(16),
  },
  locationItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: s(15),
    borderRadius: s(8),
    borderWidth: borderWidth(2),
    borderColor: '#e9ecef',
  },
  locationIcon: {
    marginRight: s(12),
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: '#333',
    marginBottom: v(2),
  },
  locationDetails: {
    fontSize: fs(12),
    lineHeight: fs(16),
    fontFamily: GTFonts.regular,
    color: '#666',
  },
  defaultBadge: {
    backgroundColor: '#1560bd',
    paddingHorizontal: s(8),
    paddingVertical: s(4),
    borderRadius: s(12),
  },
  defaultBadgeText: {
    fontSize: fs(10),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: 'white',
  },
  currentLocationItem: {
    backgroundColor: theme.colors.successLight,
    borderColor: theme.colors.successLight,
  },
  badgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(5),
  },
  currentLocationIcon: {
    marginLeft: s(5),
  },
  locationItemWeb: {
    ...(Platform.OS === 'web'
      ? ({
          cursor: 'pointer',
          transition: 'background-color 0.15s ease-out',
        } as any)
      : {}),
  },
  locationItemHovered: {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  footerContainer: {
    backgroundColor: theme.colors.surface,
    paddingHorizontal: s(8),
    paddingVertical: s(20),
    ...(Platform.OS === 'web' && {
      paddingHorizontal: s(24),
    }),
  },
  addNewLocationButton: {
    width: '100%',
    backgroundColor: '#1560bd',
    paddingVertical: s(14),
    borderRadius: s(12),
    alignItems: 'center',
  },
  addNewLocationButtonText: {
    color: 'white',
    fontFamily: PoppinsFonts.regular,
    fontWeight: '700',
    fontSize: fs(16),
  },
});

export default LocationPicker;
